import {
  Box,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import ElevationScroll from "../../ui/ElevationScroll";
import ArrowLeftIcon from "../../ui/icons/ArrowLeftIcon";

interface MainAppBarProps extends Omit<MuiAppBarProps, 'title'> {
  open?: boolean;
  darkMode?: boolean;
  beforeGoBack?: (e?: any) => void;
  preventGoBack?: boolean;
  title?: string | React.ReactNode;
  extraAction?: React.ReactNode;
  hideGoBack?: boolean;
  hideTitle?: boolean;
  position?: 'relative' | 'fixed';
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<MainAppBarProps>(({ theme, position }) => ({
  color: 'inherit',
  zIndex: theme.zIndex.drawer,
  backgroundColor: "transparent",
  minHeight: 64,
  display: 'flex',
  justifyContent: 'center',
  position: position || 'fixed',
  transition: theme.transitions.create(
    ["box-shadow", "width", "margin", "color"],
    {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.leavingScreen,
    }
  ),
  "&.elevated": {
    backgroundColor: `${theme.palette.background.default} !important`,
    transition: theme.transitions.create(
      ["box-shadow", "width", "margin", "color"],
      {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.enteringScreen,
      }
    ),
  },
}));

const MainAppBar: React.FC<MainAppBarProps> = ({
  open,
  beforeGoBack,
  preventGoBack = false,
  darkMode = false,
  hideGoBack = false,
  hideTitle = false,
  title,
  extraAction,
  position = 'fixed',
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [canGoBack, setCanGoBack] = React.useState(false);

  // const showBackButton = canGoBack && !hideGoBack;

  const renderTitle = () => {
    if (hideTitle) {
      return null;
    }

    if (typeof title === 'string') {
      return <Typography variant="h2">{title}</Typography>;
    }

    if (React.isValidElement(title)) {
      return title;
    }

    if (isDesktop) {
      return null;
    }

    return (
      <Link to={ROUTES.HOME}>
        <img
          src={darkMode ? "/logo-light.png" : "/logo-dark.png"}
          alt="Mimo - QLCT"
          loading="lazy"
          height={23}
        />
      </Link>
    );
  };

  const handleOnBack = () => {
    if (beforeGoBack) {
      beforeGoBack();
    }
    if (preventGoBack) return;

    if (canGoBack) {
      navigate(-1);
    } else {
      navigate(ROUTES.HOME, { replace: true });
    }
  };

  useEffect(() => {
    const hasHistory = window.history?.length > 1;
    const isHomePage = [ROUTES.HOME, ROUTES.ASSETS, ""].includes(location.pathname);
    const isMobileDialogCreateWalletOpen = location?.state?.createWallet && !isDesktop;

    setCanGoBack(hasHistory && (!isHomePage || !!beforeGoBack || isMobileDialogCreateWalletOpen));
  }, [location.pathname, location.state, isDesktop, beforeGoBack]);

  return (
    <>
      <ElevationScroll {...props}>
        <AppBar
          open={open}
          position={position}
          className={props.elevation ? "elevated" : ""}
          sx={{
            ...props.sx,
            ...(isDesktop && {
              marginLeft: theme.mixins.sidebar.width,
              width: `calc(100% - ${theme.mixins.sidebar.width}px)`,
            })
          }}
        >
          <Stack paddingX={2} direction="row" width="100%" justifyContent="space-between" alignItems="center">
            {canGoBack && !hideGoBack ? (
              <IconButton
                color="inherit"
                onClick={!hideGoBack ? handleOnBack : undefined}
                style={{
                  position: 'absolute',
                  color: hideGoBack
                    ? "transparent"
                    : darkMode
                      ? theme.palette.common.white
                      : "inherit",
                }}
              >
                <ArrowLeftIcon />
              </IconButton>
            ) : null
            }
            <Stack direction='row' justifyContent="space-between" alignItems="center" width="100%">
              <Stack></Stack>
              {renderTitle()}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: hideTitle ? '100%' : 'auto' }}>
                {extraAction}
              </Stack>
            </Stack>
          </Stack>
        </AppBar>
      </ElevationScroll>
      <Box height={64} />
    </>
  );
};

export default MainAppBar;
